import { ErrorMessage, Field, Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import * as Yup from "yup";
import type { StudioFormState } from "../schema";
import Loading from "./Loading";
import { StudioServices } from "../assets/studio/studioServicesData";

const StudioForm = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (
    values: StudioFormState,
    { resetForm }: FormikHelpers<StudioFormState>
  ) => {
    setLoading(true);

    try {
      const API_URL = "https://api.emailjs.com/api/v1.0/email/send";

      const payload = {
        service_id: import.meta.env.VITE_STUDIO_EMAILJS_SERVICE_ID,
        template_id: import.meta.env.VITE_STUDIO_EMAILJS_TEMPLATE_ID,
        user_id: import.meta.env.VITE_STUDIO_EMAILJS_PUBLIC_KEY,
        template_params: {
          name: values.name,
          email: values.email,
          services: values.service.join(", "),
          comments: values.comment,
        },
      };

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Failed to send: ${res.statusText}`);
      }

      toast.success("Form submitted, we will contact you soon");
      resetForm();
    } catch (error) {
      toast.error(`We couldn't process your request please try again later`);
      console.log(error);
    }
    setLoading(false);
  };
  return (
    <>
      <Formik
        initialValues={{ name: "", email: "", service: [] as string[], comment: "" }}
        validationSchema={Yup.object({
          name: Yup.string().required("Name is required"),
          email: Yup.string().email("Invalid email address").required("Email is required"),
          service: Yup.array()
            .min(1, "At least one service is required")
            .required("Service is required"),
        })}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, errors, touched }) => (
          <Form className="col-span-full grid grid-cols-subgrid gap-y-4 lg:gap-y-6 h-fit self-center">
            <div className="col-span-full flex flex-col gap-2">
              <label className="font-condensedBoldItalic" htmlFor="name">
                Name:
              </label>
              <Field
                name="name"
                type="text"
                value={values.name}
                onChange={handleChange}
                className={`border-[0.5px] border-black h-11 p-2 ${
                  Boolean(errors.name && touched.name) && "border-red-600"
                } w-full`}
              />
              <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div className="col-span-full flex flex-col gap-2">
              <label className="font-condensedBoldItalic" htmlFor="email">
                Email Address:
              </label>
              <Field
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                className={`border-[0.5px] border-black h-11 p-2 ${
                  Boolean(errors.email && touched.email) && "border-red-600"
                } w-full`}
              />
              <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div className="col-span-full flex flex-col gap-2">
              <span className="font-condensedBoldItalic">Services required:</span>
              <div className="grid grid-cols-2 justify-between gap-y-4">
                {StudioServices.map((service) => (
                  <label
                    key={service.icon}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Field
                      type="checkbox"
                      name="service"
                      value={service.name}
                      checked={values.service.includes(service.name)}
                      className="accent-cultiv-red size-5"
                    />
                    {service.name}
                  </label>
                ))}
              </div>
              <ErrorMessage name="service" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div className="col-span-full flex flex-col gap-2">
              <label className="font-condensedBoldItalic" htmlFor="comment">
                Your comments
              </label>
              <textarea
                name="comment"
                value={values.comment}
                onChange={handleChange}
                className="border-[0.5px] border-black h-[120px] p-2 w-full"
              />
            </div>
            {loading ? (
              <div className="col-span-2 col-start-2">
                <Loading />
              </div>
            ) : (
              <div
                className={`col-span-full grid grid-cols-subgrid gap-y-4 items-center group h-fit`}
              >
                <button
                  type="submit"
                  onClick={() => handleSubmit}
                  className={`flex items-center w-full text-sm md:text-lg lg:text-base xl:text-xl row-start-2 col-span-2 justify-center font-Light text-white hover:text-black hover:bg-transparent bg-cultiv-red border-1 hover:border-black border-cultiv-red transition-all duration-300 ease-in-out px-6 py-[6px] overflow-hidden`}
                >
                  Submit
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
      <Toaster />
    </>
  );
};

export default StudioForm;
